/**
 * Letterbox / uniform-border detection.
 *
 * Users routinely upload screenshots of a photo rather than the photo: a shot
 * of a house with black bars above and below, or a flat white gutter left by a
 * messenger. The building is fine, but the frame is mostly dead area — and the
 * validation model reads that as "the building is small and distant" and asks
 * the user to re-crop (analyze-building-image, guideline #10). They rarely do.
 *
 * A uniform border has an exact answer, so it is found here rather than asked
 * of a model: scan inwards from each edge while the line stays flat and keeps
 * the colour of the outermost line. Costs a few ms, cannot hallucinate.
 *
 * This module is deliberately pure and DOM-free: the same constants and the
 * same decisions have to hold on Apple and Android, and a shared spec only
 * stays shared if it is one readable function.
 */

export interface TrimRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** RGBA pixels, as produced by `CanvasRenderingContext2D.getImageData`. */
export interface PixelSource {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/**
 * Shared spec — these numbers are the contract across WEB / Apple / Android.
 * Changing one here means changing it in the other two.
 */
export const TRIM_SPEC = {
  /** Max distance (per channel) from the border colour for one pixel to match. */
  driftTolerance: 8,
  /**
   * Share of a line's samples that must match the border colour for the line to
   * belong to the border.
   *
   * Deliberately not 100%: real bars are not clean. The outermost row of a JPEG
   * carries compression noise, and screenshots put things ON the bar — a close
   * button, a status bar, a caption. Demanding a perfectly uniform line meant
   * one 40px "×" icon stopped the walk 600 rows early, and noise in row 0 stopped
   * it before it began. A photo row matches a black bar in essentially none of
   * its pixels, so the margin here is enormous either way.
   */
  borderPurity: 0.85,
  /** Roughly how many pixels to sample along a line. Full scans buy nothing. */
  samplesPerLine: 128,
  /** A border smaller than this share of its side is JPEG edge noise, not a bar. */
  minBandRatio: 0.015,
  /**
   * Trim more than this share of an axis and it is not a letterbox any more.
   *
   * This is deliberately loose, because a phone screenshot of a photo is loose
   * by construction: a 4:3 photo shown full-width on a 9:16 screen leaves ~58%
   * of the frame as bars, and that is the single most common upload we are
   * trying to fix. The real guard against eating a photo is the per-pixel drift
   * check — a clear sky or a blank wall shifts as it recedes and drops below
   * the purity threshold within a few rows, where a rendered bar holds one
   * value for hundreds.
   */
  maxTrimRatio: 0.75,
  /**
   * Whatever survives must still be a plausible photo. A rect this small is a
   * sliver in a mostly-empty frame; that is the validation model's call to
   * make, not a border to remove silently.
   */
  minContentAreaRatio: 0.2,

  // --- Photo embedded in a page of interface (below) --------------------

  /** A line this full of the page colour is chrome (background, text, icons). */
  pageLineShare: 0.85,
  /** A line this empty of it is inside a photograph. */
  photoLineShare: 0.25,
  /**
   * A colour covering less than this much of the frame is not a page at all.
   * Only a sanity floor: a photo-viewer screenshot, where the photo fills three
   * quarters of the screen, leaves the page itself under a fifth of the frame,
   * so anything stricter rejects the most common shape of this class.
   */
  minPageColourShare: 0.05,
  /**
   * Consecutive page-colour rows required on EACH side of the photo band, as a
   * share of the frame. This, not how much page there is, is what tells a
   * screenshot from a photograph: chrome is a solid block above and below the
   * photo, while a photograph's uniform sky is a block on one side with the
   * photo's own content on the other. Kept small because it only has to mean
   * "a block, not a stray line" — the work is done by demanding BOTH sides in
   * the same colour, and measured screenshots clear it several times over.
   */
  minFlankingPageRun: 0.005,
  /** An embedded photo may be smaller than a letterboxed one: it is flanked by
   * positive evidence (page chrome above and below), not just by flat bars. */
  minEmbeddedAreaRatio: 0.1,

  /**
   * Longest side of the working copy the scan runs on. A full 4096px frame means
   * a ~50MB `getImageData` buffer on a phone for a decision that only needs to
   * be accurate to a few pixels; 1024 costs ~3MB and lands within a couple of
   * source pixels of the same boundary.
   */
  workingDimension: 1024,
} as const;

type Rgb = { r: number; g: number; b: number };

/**
 * Sampled pixels of one row (`axis: 'row'`) or column, alpha ignored. `within`
 * limits the line to a sub-rectangle, so a scan can run inside a frame that an
 * earlier pass already narrowed down.
 */
function sampleLine(
  src: PixelSource,
  index: number,
  axis: 'row' | 'column',
  within: TrimRect = { x: 0, y: 0, width: src.width, height: src.height }
): Rgb[] {
  const from = axis === 'row' ? within.x : within.y;
  const length = axis === 'row' ? within.width : within.height;
  const step = Math.max(1, Math.floor(length / TRIM_SPEC.samplesPerLine));
  const samples: Rgb[] = [];

  for (let offsetInLine = 0; offsetInLine < length; offsetInLine += step) {
    const i = from + offsetInLine;
    const x = axis === 'row' ? i : index;
    const y = axis === 'row' ? index : i;
    const offset = (y * src.width + x) * 4;

    samples.push({
      r: src.data[offset] ?? 0,
      g: src.data[offset + 1] ?? 0,
      b: src.data[offset + 2] ?? 0,
    });
  }

  return samples;
}

/**
 * The border's colour, read from its outermost line. Median rather than mean:
 * an icon or a caption sitting on the bar drags a mean towards itself, while
 * the median simply ignores a minority of odd pixels.
 */
function medianColour(samples: Rgb[]): Rgb {
  const channel = (pick: (p: Rgb) => number) => {
    const values = samples.map(pick).sort((a, b) => a - b);
    return values[Math.floor(values.length / 2)] ?? 0;
  };

  return {
    r: channel((p) => p.r),
    g: channel((p) => p.g),
    b: channel((p) => p.b),
  };
}

/** Share of samples within `driftTolerance` of a reference colour, per channel. */
function purity(samples: Rgb[], reference: Rgb): number {
  if (samples.length === 0) return 0;
  const drift = TRIM_SPEC.driftTolerance;

  const matching = samples.filter(
    (p) =>
      Math.abs(p.r - reference.r) <= drift &&
      Math.abs(p.g - reference.g) <= drift &&
      Math.abs(p.b - reference.b) <= drift
  ).length;

  return matching / samples.length;
}

/**
 * How many lines to peel off one side: walk inwards while every line stays flat
 * and keeps the colour the border opened with. `limit` stops the walk before
 * the opposite border can be reached on a uniform image.
 */
function scanSide(
  src: PixelSource,
  axis: 'row' | 'column',
  from: 'start' | 'end',
  limit: number
): number {
  const total = axis === 'row' ? src.height : src.width;
  const first = from === 'start' ? 0 : total - 1;
  const direction = from === 'start' ? 1 : -1;

  const opening = sampleLine(src, first, axis);
  const border = medianColour(opening);
  if (purity(opening, border) < TRIM_SPEC.borderPurity) return 0;

  let band = 1;
  while (band < limit) {
    const line = sampleLine(src, first + band * direction, axis);
    if (purity(line, border) < TRIM_SPEC.borderPurity) break;
    band++;
  }

  return band;
}

/** Drop a band that is too thin to be a real border. */
function applyFloor(band: number, side: number): number {
  return band >= side * TRIM_SPEC.minBandRatio ? band : 0;
}

/**
 * The content rectangle inside a uniform border, or `null` when the image has
 * no border worth removing (the common case — leave those frames alone).
 *
 * Each axis is decided on its own, and an axis whose two bands together eat
 * more than `maxTrimRatio` is left untouched: that is no longer a letterbox
 * but a flat sky, a blank wall, or a near-empty frame, and cutting it would
 * throw away the photo instead of the packaging.
 */
export function detectContentBounds(src: PixelSource): TrimRect | null {
  if (src.width < 8 || src.height < 8) return null;

  // Never let a side's walk cross the midpoint: on a fully uniform image both
  // sides would otherwise consume the whole axis and meet in the middle.
  const verticalLimit = Math.floor(src.height / 2);
  const horizontalLimit = Math.floor(src.width / 2);

  let top = applyFloor(scanSide(src, 'row', 'start', verticalLimit), src.height);
  let bottom = applyFloor(scanSide(src, 'row', 'end', verticalLimit), src.height);
  let left = applyFloor(
    scanSide(src, 'column', 'start', horizontalLimit),
    src.width
  );
  let right = applyFloor(
    scanSide(src, 'column', 'end', horizontalLimit),
    src.width
  );

  // Bars come in pairs. Letterboxing and pillarboxing both pad an axis on BOTH
  // sides, because they exist to fit one aspect ratio inside another. A band on
  // one side only is far more likely to be part of the photograph: an
  // overexposed white sky above a house is as flat and as steady as any
  // rendered bar, and cropping it away would silently rewrite a framing the
  // user chose. Requiring the pair costs us the rare one-sided letterbox and
  // buys immunity from the whole class.
  if (top === 0 || bottom === 0) {
    top = 0;
    bottom = 0;
  }
  if (left === 0 || right === 0) {
    left = 0;
    right = 0;
  }

  if (top + bottom > src.height * TRIM_SPEC.maxTrimRatio) {
    top = 0;
    bottom = 0;
  }
  if (left + right > src.width * TRIM_SPEC.maxTrimRatio) {
    left = 0;
    right = 0;
  }

  if (top === 0 && bottom === 0 && left === 0 && right === 0) return null;

  const area = (src.width - left - right) * (src.height - top - bottom);
  if (area < src.width * src.height * TRIM_SPEC.minContentAreaRatio) return null;

  return {
    x: left,
    y: top,
    width: src.width - left - right,
    height: src.height - top - bottom,
  };
}

/**
 * The colour a screenshot's page is painted in, plus how much of the frame it
 * covers. Taken as the most common colour on a coarse grid rather than from an
 * edge line: a page's background is defined by how much of it there is, and the
 * outermost line of a screenshot is as likely to be a status bar as the page.
 */
function dominantColour(
  src: PixelSource,
  within: TrimRect
): { colour: Rgb; share: number } {
  const buckets = new Map<number, number>();
  let total = 0;

  // Quantised to 4 levels per channel: fine enough to keep #FFFFFF and #F7F7F7
  // apart, coarse enough that compression noise lands in one bucket.
  for (let y = within.y; y < within.y + within.height; y += 5) {
    for (let x = within.x; x < within.x + within.width; x += 5) {
      const offset = (y * src.width + x) * 4;
      const key =
        (((src.data[offset] ?? 0) >> 2) << 12) |
        (((src.data[offset + 1] ?? 0) >> 2) << 6) |
        ((src.data[offset + 2] ?? 0) >> 2);
      buckets.set(key, (buckets.get(key) ?? 0) + 1);
      total++;
    }
  }

  let bestKey = 0;
  let bestCount = 0;
  for (const [key, count] of buckets) {
    if (count > bestCount) {
      bestKey = key;
      bestCount = count;
    }
  }

  return {
    colour: {
      r: (((bestKey >> 12) & 0x3f) << 2) + 2,
      g: (((bestKey >> 6) & 0x3f) << 2) + 2,
      b: ((bestKey & 0x3f) << 2) + 2,
    },
    share: total === 0 ? 0 : bestCount / total,
  };
}

/** Start and length of the longest run of `true` in `flags`. */
function longestRun(flags: boolean[]): { start: number; length: number } {
  let best = { start: 0, length: 0 };
  let runStart = -1;

  for (let i = 0; i <= flags.length; i++) {
    if (flags[i]) {
      if (runStart < 0) runStart = i;
    } else if (runStart >= 0) {
      if (i - runStart > best.length) best = { start: runStart, length: i - runStart };
      runStart = -1;
    }
  }

  return best;
}

/**
 * The photograph inside a page of interface, or `null` when the frame is not
 * that kind of image.
 *
 * The second shape people upload: not a photo with bars around it, but a
 * screenshot of a feed or a chat, where the photo sits in the middle of a page
 * with text and controls above and below it. There is no uniform border to
 * remove, so `detectContentBounds` correctly finds nothing.
 *
 * What separates the photo from the page is how much of the page's own colour
 * a line carries. A line of interface is mostly background even when it holds
 * text or icons — glyphs are a minority of its pixels. A line inside a
 * photograph contains almost none of it. Measured on a real feed screenshot the
 * two populations do not overlap: ~0.02 inside the photo against 0.6–1.0
 * everywhere else.
 *
 * The guard that keeps this off ordinary photographs, where a bright sky can
 * also dominate the colour histogram, is that the photo band must be flanked by
 * a solid run of page chrome BOTH above and below. A photo of a house under a
 * white sky fails it: the sky is at the top, and below the house there is only
 * more house.
 */
export function detectEmbeddedPhoto(
  src: PixelSource,
  within: TrimRect = { x: 0, y: 0, width: src.width, height: src.height }
): TrimRect | null {
  if (within.width < 8 || within.height < 8) return null;

  const page = dominantColour(src, within);
  if (page.share < TRIM_SPEC.minPageColourShare) return null;

  const rowShares: number[] = [];
  for (let y = within.y; y < within.y + within.height; y++) {
    rowShares.push(purity(sampleLine(src, y, 'row', within), page.colour));
  }

  const band = longestRun(
    rowShares.map((share) => share <= TRIM_SPEC.photoLineShare)
  );
  if (band.length === 0) return null;

  // Page chrome must flank the band on both sides, as a solid run rather than a
  // stray line. This is what tells a photo embedded in a page apart from a
  // photograph whose own sky happens to be uniform: the sky is a block above
  // the content with nothing but content below it.
  const isPage = (share: number) => share >= TRIM_SPEC.pageLineShare;
  const flankingRun = Math.max(
    4,
    Math.round(within.height * TRIM_SPEC.minFlankingPageRun)
  );
  const above = longestRun(rowShares.slice(0, band.start).map(isPage));
  const below = longestRun(
    rowShares.slice(band.start + band.length).map(isPage)
  );
  if (above.length < flankingRun || below.length < flankingRun) return null;

  const top = within.y + band.start;
  const height = band.length;

  const columnShares: number[] = [];
  for (let x = within.x; x < within.x + within.width; x++) {
    columnShares.push(
      purity(
        sampleLine(src, x, 'column', {
          x: within.x,
          y: top,
          width: within.width,
          height,
        }),
        page.colour
      )
    );
  }

  const span = longestRun(
    columnShares.map((share) => share <= TRIM_SPEC.photoLineShare)
  );
  if (span.length === 0) return null;

  const area = span.length * height;
  if (area < within.width * within.height * TRIM_SPEC.minEmbeddedAreaRatio) {
    return null;
  }

  return { x: within.x + span.start, y: top, width: span.length, height };
}

/**
 * The photograph inside an upload, whichever way it is packaged — or `null`
 * when the frame is already just a photograph.
 *
 * Single entry point for callers. The page pass runs FIRST and the border pass
 * is the fallback, because the page pass is the more specific of the two: it
 * asks where the photograph is, while the border pass only asks what is not
 * photograph at the edges.
 *
 * Order matters, and getting it wrong is subtle. A photo-viewer screenshot has
 * white chrome at the top and a white toolbar at the bottom — a uniform pair by
 * every formal test — so the border pass answers it happily and returns the
 * photo WITH the thumbnail strip still attached below it, because that strip is
 * not a uniform border. The page pass reads the same frame properly and stops at
 * the white separator above the strip. Asking the specific question first, and
 * the crude one only when it has no answer, is what keeps that strip out.
 */
export function findPhotoBounds(src: PixelSource): TrimRect | null {
  return detectEmbeddedPhoto(src) ?? detectContentBounds(src);
}

/**
 * Scale a rect found on a downscaled working copy back to natural pixels,
 * rounding OUTWARDS. A downscale blurs the bar/photo boundary by a pixel or
 * two, and keeping a sliver of the bar is harmless where eating a sliver of
 * the building is not.
 */
export function scaleRectToNatural(
  rect: TrimRect,
  scale: number,
  natural: { width: number; height: number }
): TrimRect {
  const x = Math.max(0, Math.floor(rect.x * scale));
  const y = Math.max(0, Math.floor(rect.y * scale));
  const right = Math.min(natural.width, Math.ceil((rect.x + rect.width) * scale));
  const bottom = Math.min(
    natural.height,
    Math.ceil((rect.y + rect.height) * scale)
  );

  return { x, y, width: right - x, height: bottom - y };
}
